import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  LocationOn,
  Email,
  Phone,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  ArrowForward as ArrowIcon,
  Language as GlobeIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
// Import partner logos
import momulimisaLogo from '../assests/momulimisa.png';
import bodaaiLogo from '../assests/bodaai.png';
import quakeLogo from '../assests/quake.png';
import ctdruLogo from '../assests/ctdru.png';

const Footer = () => {
  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Products', to: '/products' },
    { label: 'About Us', to: '/aboutus' },
  ];

  const services = [
    'Machine Translation',
    'Text-to-Speech (TTS)',
    'Speech Recognition (ASR)',
    'Agricultural Chatbots',
    'Policy Translation',
    'AI Analytics',
  ];

  return (
    <Box sx={{
      backgroundColor: '#030712',
      color: 'white',
      pt: { xs: 8, md: 12 },
      pb: { xs: 4, md: 6 },
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(245,158,11, 0.5), transparent)',
        zIndex: 1,
      },
    }}>
      {/* Background radial glows */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0,
        background: 'radial-gradient(ellipse at 15% 50%, rgba(245,158,11, 0.07) 0%, transparent 55%), radial-gradient(ellipse at 85% 20%, rgba(245,158,11, 0.04) 0%, transparent 50%)',
      }} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 5, md: 6, lg: 8 }}>
          {/* Company Info — wider column */}
          <Grid item xs={12} md={4}>
            {/* Logo */}
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #FCD34D 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '2rem',
                  letterSpacing: '-0.03em',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  lineHeight: 1,
                }}
              >
                PHOSAI
              </Typography>
              <Typography sx={{ color: '#D97706', fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', mt: 0.3 }}>
                AI for Africa
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{ color: '#6B7280', lineHeight: 1.75, mb: 3.5, fontSize: '0.95rem', mt: 2.5 }}
            >
              World-class AI company specializing in machine translation for African languages.
              Bridging linguistic barriers with cutting-edge technology while preserving cultural authenticity.
            </Typography>

            {/* Language tags */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3.5 }}>
              {['Luganda', 'Swahili', 'Kinyarwanda', 'Zulu', 'Xhosa'].map(lang => (
                <Chip key={lang} label={lang} size="small" sx={{
                  height: 24,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: 'rgba(245,158,11, 0.12)',
                  color: '#A78BFA',
                  border: '1px solid rgba(245,158,11, 0.2)',
                  '&:hover': { backgroundColor: 'rgba(245,158,11, 0.2)' },
                }} />
              ))}
            </Box>

            {/* Social Media */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {[
                { icon: <Facebook sx={{ fontSize: 18 }} />, color: '#1877F2' },
                { icon: <Twitter sx={{ fontSize: 18 }} />, color: '#1DA1F2' },
                { icon: <Instagram sx={{ fontSize: 18 }} />, color: '#E4405F' },
                { icon: <LinkedIn sx={{ fontSize: 18 }} />, color: '#0A66C2' },
              ].map((social, index) => (
                <IconButton
                  key={index}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: '#9CA3AF',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    width: 38,
                    height: 38,
                    borderRadius: '10px',
                    '&:hover': {
                      backgroundColor: social.color,
                      color: 'white',
                      border: `1px solid ${social.color}`,
                      transform: 'translateY(-3px)',
                      boxShadow: `0 8px 20px ${social.color}40`,
                    },
                    transition: 'all 0.25s ease',
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="overline" sx={{ fontWeight: 700, color: '#F59E0B', letterSpacing: '0.12em', fontSize: '0.7rem', mb: 2.5, display: 'block' }}>
              Navigation
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {navLinks.map((link) => (
                <Box
                  key={link.label}
                  component={Link}
                  to={link.to}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: '#9CA3AF',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: '#FCD34D',
                      transform: 'translateX(4px)',
                      '& .arrow': { opacity: 1 },
                    },
                  }}
                >
                  <ArrowIcon className="arrow" sx={{ fontSize: 14, opacity: 0, transition: 'opacity 0.2s ease' }} />
                  {link.label}
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Core Services */}
          <Grid item xs={6} sm={4} md={3}>
            <Typography variant="overline" sx={{ fontWeight: 700, color: '#F59E0B', letterSpacing: '0.12em', fontSize: '0.7rem', mb: 2.5, display: 'block' }}>
              AI Services
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {services.map((service, index) => (
                <Typography key={index} variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem', lineHeight: 1.5, fontWeight: 500 }}>
                  {service}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Contact */}
          <Grid item xs={12} sm={4} md={3}>
            <Typography variant="overline" sx={{ fontWeight: 700, color: '#F59E0B', letterSpacing: '0.12em', fontSize: '0.7rem', mb: 2.5, display: 'block' }}>
              Contact Us
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { icon: <LocationOn sx={{ fontSize: 16 }} />, text: 'Makerere University, Kampala, Uganda' },
                { icon: <Email sx={{ fontSize: 16 }} />, text: 'phosaico@gmail.com' },
                { icon: <Phone sx={{ fontSize: 16 }} />, text: '+256 750 371 313' },
                { icon: <Phone sx={{ fontSize: 16 }} />, text: '+256 702 560 814' },
              ].map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{ color: '#F59E0B', mt: 0.1, flexShrink: 0 }}>{item.icon}</Box>
                  <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}

              {/* Strategic Partners */}
              <Box sx={{ mt: 1 }}>
                <Typography variant="overline" sx={{ fontWeight: 700, color: '#F59E0B', letterSpacing: '0.12em', fontSize: '0.65rem', mb: 1.5, display: 'block' }}>
                  Partners
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {[
                    { name: 'M-Omulimisa', logo: momulimisaLogo, url: 'https://m-omulimisa.com' },
                    { name: 'Boda AI', logo: bodaaiLogo, url: 'http://www.boda-ai-factory.com' },
                    { name: 'Quake', logo: quakeLogo, url: 'https://quakebrandfix.com' },
                    { name: 'CTDRU', logo: ctdruLogo, url: 'https://ctdrug.org' },
                  ].map((p, i) => (
                    <Box
                      key={i}
                      component="a"
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={p.name}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.12)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                        },
                      }}
                    >
                      <img src={p.logo} alt={p.name} style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 4 }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider sx={{ my: { xs: 5, md: 6 }, borderColor: 'rgba(255, 255, 255, 0.06)' }} />

        {/* Bottom Bar */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}>
          <Typography variant="body2" sx={{ color: '#4B5563', fontSize: '0.82rem' }}>
            © 2025 PHOSAI. All rights reserved. Supporting 10+ African Languages.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <GlobeIcon sx={{ fontSize: 14, color: '#F59E0B' }} />
              <Typography variant="body2" sx={{ color: '#4B5563', fontSize: '0.82rem' }}>
                Made in East Africa
              </Typography>
            </Box>
            <Chip
              label="AI-Powered"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 700,
                backgroundColor: 'rgba(245,158,11, 0.15)',
                color: '#F59E0B',
                border: '1px solid rgba(245,158,11, 0.25)',
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
